import { memo as Memo, useMemo, useDeferredValue, useState, useRef, useEffect, useCallback } from 'react';
import gsap from 'gsap';
import { useRouter } from 'next/router';

//* HOC's
import { withUIContext, withDataContext, withLanguageContext } from 'context';

//* Components
import Logo from 'components/common/Logo';
import DesktopMenu from './DesktopMenu';
import MobileMenu from './MobileMenu';
import LanguageSwitcher from 'components/common/LanguageSwitcher';

//* Style
import HeaderStyle from './style';

const AppHeader = Memo(({ transparentHeader, winWidth, global, screenSizes, selectedLang }) => {
	//! Refs
	const headerRef = useRef();

	//! Router
	const router = useRouter();

	//! States
	const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
	const [show, setShow] = useState(true);
	const [topPosition, setTopPosition] = useState(0);
	const [mainMinHeight, setMainMinHeight] = useState();
	const [transparent, setTransparent] = useState(true);

	//! Close Mobile Menu
	useEffect(() => {
		setMobileMenuOpened(false);
	}, [router.asPath]);

	//! Data
	const deferredGlobal = useDeferredValue(global);
	const deferredSelectedLang = useDeferredValue(selectedLang);
	const menuData = useMemo(() => deferredGlobal?.[deferredSelectedLang]?.menu_bar, [deferredGlobal, deferredSelectedLang]);

	//! Did Mount
	useEffect(() => {
		window.addEventListener('scroll', onScroll);

		mobileMenuOpened ? document.body.classList.add('hidden') : document.body.classList.remove('hidden');

		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [mobileMenuOpened, transparentHeader, router.asPath]);

	//! Show / Hide Header
	const oldScroll = useRef(0);
	const onScroll = useCallback(() => {
		if (window.scrollY > oldScroll.current && window.scrollY > 0 && !mobileMenuOpened) {
			setShow(false);
		} else if (window.scrollY < oldScroll.current || window.scrollY <= 0 || mobileMenuOpened) {
			setShow(true);
		}

		setTransparent(transparentHeader && window.scrollY <= window.innerHeight * 0.8 && router.asPath !== '/things-to-do/bucket-list/');

		oldScroll.current = window.scrollY;
	}, [mobileMenuOpened, transparentHeader, router.asPath]);

	useEffect(() => {
		onScroll();
	}, [router.asPath, transparentHeader, onScroll]);

	useEffect(() => {
		gsap.killTweensOf(headerRef.current);

		show && gsap.set(headerRef.current, { display: 'flex' });
		gsap.to(headerRef.current, {
			duration: window.scrollY === 0 ? 0.8 : 0.3,
			y: show ? '0%' : '-200%',
		}).then(() => !show && gsap.set(headerRef.current, { display: !show ? 'none' : 'flex' }));
	}, [show, transparentHeader]);

	//! First Show
	useEffect(() => {
		onScroll();
		gsap.to(headerRef.current, { duration: 0.8, delay: 0.5, y: 0 });
	}, []);

	//! Top Position
	useEffect(() => {
		if (headerRef.current) {
			const headerHeight = headerRef.current.getBoundingClientRect().height;
			topPosition !== headerHeight && setTopPosition(headerHeight);
			mainMinHeight !== window.innerHeight && setMainMinHeight(window.innerHeight);
		}
	}, [winWidth, headerRef.current, topPosition]);

	return (
		<>
			{winWidth < screenSizes.desktopSizeXS && (
				<MobileMenu
					isOpen={mobileMenuOpened}
					openMenu={() => setMobileMenuOpened(!mobileMenuOpened)}
					menuData={menuData}
					topPosition={topPosition}
				/>
			)}

			<HeaderStyle
				topPosition={topPosition}
				mainMinHeight={mainMinHeight}
				ref={headerRef}
				className={`header ${transparentHeader && router.asPath !== '/things-to-do/bucket-list/' ? 'disable-header-margin' : ''} ${transparent && !mobileMenuOpened ? 'transparent-header secondaryColor1' : 'secondaryColor1-bg'}`}
			>
				<Logo />

				{winWidth < screenSizes.desktopSizeXS ? (
					<div className='mobile-right-cont'>
						<LanguageSwitcher />

						<span
							onClick={() => setMobileMenuOpened(!mobileMenuOpened)}
							className={`burger-icon ${mobileMenuOpened ? 'active' : ''} ${transparent && !mobileMenuOpened ? 'light-bg' : ''} `}
						/>
					</div>
				) : (
					<DesktopMenu menuData={menuData} />
				)}
			</HeaderStyle>
		</>
	);
});

export default withLanguageContext(withDataContext(withUIContext(AppHeader, ['screenSizes', 'winWidth']), ['global']), ['selectedLang']);
